/**
 * A form's section LAYOUT on the CRF Builder page (CRF design spec §C1/§C2/§C8,
 * docs/crf_design_spec.md, 2026-09-18).
 *
 * Sections are a question list, a repeating table (one row per record, rows
 * counted by `expectedRows`) or a grid (PRE-PRINTED rows — a chemistry panel's
 * analytes, a body-system exam — with the same columns on every row).
 *
 * WHY THIS MODULE EXISTS. Generation returned the structure pass's sections,
 * grids and all, and the page kept them on `form.structure` where nothing read
 * them: the form's own sections were never built from them, so the next save
 * stored a flat list and the grid was gone. And every carry site copied the
 * section kind by hand as "repeating or nothing", which would have dropped a
 * grid even if one had been stored. One helper now carries the layout for
 * read-back, save and every download.
 *
 * Pure, so `npm run test:web` can reach it (`web` has no test runner).
 */
import { carriedSectionFields } from './crfCarriedFields';
import { normaliseGridSections, type GridVariableLike } from '../../../../functions/src/crf/gridNormalise';

export interface GridRow {
  label: string;
  note?: string;
  variable?: string;
}

export interface LayoutSectionFields {
  kind?: 'repeating' | 'grid';
  expectedRows?: number;
  rowEntities?: GridRow[];
  gridLabelHeader?: string;
  blankRows?: number;
}

const text = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

function cleanRows(rows: unknown): GridRow[] {
  if (!Array.isArray(rows)) return [];
  const out: GridRow[] = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const { label, note, variable } = r as Record<string, unknown>;
    if (!text(label)) continue;
    out.push({
      label: label.trim(),
      ...(text(note) ? { note: note.trim() } : {}),
      ...(text(variable) ? { variable: variable.trim() } : {}),
    });
  }
  return out;
}

/**
 * The layout fields of a section: a table's kind and row count, or a grid's
 * kind, named rows, label header and blank-row count. A grid with no named
 * rows carries nothing — it is a question list, as the server stores it,
 * because the printer refuses a grid whose rows are all blank.
 */
export function layoutSectionFields(s: {
  kind?: unknown;
  expectedRows?: unknown;
  rowEntities?: unknown;
  gridLabelHeader?: unknown;
  blankRows?: unknown;
}): LayoutSectionFields {
  if (s.kind === 'repeating') {
    return {
      kind: 'repeating',
      ...(typeof s.expectedRows === 'number' && s.expectedRows > 0 ? { expectedRows: s.expectedRows } : {}),
    };
  }
  if (s.kind === 'grid') {
    const rowEntities = cleanRows(s.rowEntities);
    if (rowEntities.length === 0) return {};
    return {
      kind: 'grid',
      rowEntities,
      ...(text(s.gridLabelHeader) ? { gridLabelHeader: s.gridLabelHeader.trim() } : {}),
      ...(typeof s.blankRows === 'number' && Number.isInteger(s.blankRows) && s.blankRows >= 0
        ? { blankRows: s.blankRows } : {}),
    };
  }
  return {};
}

/**
 * A stored form's sections as the page reads them back: a grid whose rows
 * point at its own columns opens as a question list, exactly as it prints and
 * as the next save stores it (review item 7). Without this the screen showed
 * a grid that no printout or save would ever carry. The SAME array when there
 * is nothing to change.
 */
export function storedSectionsForPage<S>(sections: S[]): S[] {
  return normaliseGridSections(sections);
}

/** A section as the page holds it. */
export interface PageLayoutSection extends LayoutSectionFields {
  id: string;
  title: string;
  variableIds: string[];
  instructions?: string;
  protocolRef?: string;
  repeat?: { max?: number; label?: string };
}

/** The `sections` of the structure pass's reply. Untrusted. */
interface StructureSection {
  title?: unknown;
  kind?: unknown;
  varNames?: unknown;
  expectedRows?: unknown;
  rowEntities?: unknown;
  gridLabelHeader?: unknown;
  blankRows?: unknown;
  instructions?: unknown;
}

let sectionSeq = 0;
const sectionKey = () => `sec-gen-${Date.now().toString(36)}-${(sectionSeq++).toString(36)}`;

/**
 * The form's sections, built from the structure pass's reply after
 * generation. Returns undefined when there is no layout to keep — no
 * sections, or one plain question list — so such a form stays a legacy
 * single-section form, exactly as before.
 *
 * - Each section takes the variables it names; a variable is claimed once.
 * - A section naming none of the form's variables is dropped: it cannot print.
 * - A variable no section names goes to the LAST QUESTION LIST, never into a
 *   table, where it would print as an extra column; when every section is a
 *   table, a question list is added for it. Nothing vanishes.
 */
export function sectionsFromStructure(
  variables: Array<{ id: string; varName: string } & Partial<Omit<GridVariableLike, 'varName'>>>,
  structure: { sections?: unknown } | null | undefined,
): PageLayoutSection[] | undefined {
  // A grid whose columns are not shared by every row arrives as a question
  // list (review item 7): the server's own rule, shared, not copied
  // (functions/src/crf/gridNormalise.ts). The page then never shows, saves or
  // prints the row-by-column cross product.
  const raw = normaliseGridSections(
    structure && Array.isArray((structure as { sections?: unknown }).sections)
      ? ((structure as { sections: unknown[] }).sections as StructureSection[])
      : [],
    variables,
  );
  if (raw.length === 0) return undefined;

  const idByName = new Map<string, string>();
  for (const v of variables) if (v && text(v.varName) && !idByName.has(v.varName)) idByName.set(v.varName, v.id);

  const claimed = new Set<string>();
  const built: PageLayoutSection[] = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const names = Array.isArray(s.varNames) ? s.varNames.filter(text) : [];
    const variableIds: string[] = [];
    for (const n of names) {
      const id = idByName.get(n);
      if (id && !claimed.has(id)) { claimed.add(id); variableIds.push(id); }
    }
    if (variableIds.length === 0) continue;
    built.push({
      id: sectionKey(),
      title: text(s.title) ? s.title.trim() : '',
      variableIds,
      ...(text(s.instructions) ? { instructions: s.instructions.trim() } : {}),
      ...layoutSectionFields(s),
    });
  }
  if (built.length === 0) return undefined;

  const unclaimed = variables.filter((v) => v && !claimed.has(v.id)).map((v) => v.id);
  if (unclaimed.length > 0) {
    const lists = built.filter((s) => !s.kind);
    if (lists.length > 0) {
      lists[lists.length - 1].variableIds.push(...unclaimed);
    } else {
      built.push({ id: sectionKey(), title: '', variableIds: unclaimed });
    }
  }

  // One plain question list is no layout: leave the form as it always was.
  if (built.length === 1 && !built[0].kind) return undefined;
  return built;
}

/**
 * The sections as the save payload and every download send them: layout,
 * carried fields (instructions, citation, repeating block) and variable names.
 * Undefined for a form without sections.
 */
export function sectionsToPayload(form: {
  variables?: Array<{ id: string; varName: string }> | null;
  sections?: Array<{
    title: string;
    variableIds: string[];
    kind?: string;
    expectedRows?: number;
    rowEntities?: GridRow[];
    gridLabelHeader?: string;
    blankRows?: number;
    instructions?: string;
    protocolRef?: string;
    repeat?: { max?: number; label?: string };
  }>;
}): Array<LayoutSectionFields & { title: string; varNames: string[]; instructions?: string; protocolRef?: string; repeat?: { max?: number; label?: string } }> | undefined {
  if (!Array.isArray(form.sections) || form.sections.length === 0) return undefined;
  const nameById = new Map((form.variables || []).map((v) => [v.id, v.varName]));
  return form.sections.map((s) => ({
    title: s.title,
    ...layoutSectionFields(s),
    ...carriedSectionFields(s),
    varNames: s.variableIds.map((id) => nameById.get(id)).filter((n): n is string => Boolean(n)),
  }));
}

/**
 * Whether a form's sections are worth keeping on read-back: several sections,
 * or a single TABLE (repeating or grid). One plain question list is no layout,
 * and the page leaves such a form section-less, as it always did.
 *
 * A form whose only section is a grid used to fail this test (it asked only
 * for 'repeating'), so read-back dropped the section and the next save stored
 * a flat list: the grid was lost by opening the form.
 */
export function hasSectionLayout(sections: Array<{ kind?: unknown }>): boolean {
  if (sections.length > 1) return true;
  return sections.length === 1 && (sections[0].kind === 'repeating' || sections[0].kind === 'grid');
}

// ── Editing a section's layout on the page (CRF design spec §C9) ────────────

/** A grid's rows as editable text: one row per line, "label | note". */
export function gridRowsToText(rows: GridRow[] | undefined): string {
  return (rows || []).map((r) => (r.note ? `${r.label} | ${r.note}` : r.label)).join('\n');
}

/**
 * Editable text back into rows. Blank lines are skipped. A row whose label is
 * unchanged (ignoring case) keeps its own variable, so editing the list does
 * not detach a row from the field it records.
 */
export function parseGridRowsText(text: string, existing: GridRow[] = []): GridRow[] {
  const byLabel = new Map(existing.map((r) => [r.label.trim().toLowerCase(), r]));
  const out: GridRow[] = [];
  for (const line of String(text ?? '').split(/\r?\n/)) {
    const bar = line.indexOf(' | ');
    const label = (bar >= 0 ? line.slice(0, bar) : line).trim();
    const note = bar >= 0 ? line.slice(bar + 3).trim() : '';
    if (!label) continue;
    const prior = byLabel.get(label.toLowerCase());
    out.push({
      label,
      ...(note ? { note } : {}),
      ...(prior?.variable ? { variable: prior.variable } : {}),
    });
  }
  return out;
}

export interface SectionEditDraft {
  title: string;
  kind: string;
  expectedRows: string;
  rowsText: string;
  gridLabelHeader: string;
}

export interface SectionEditChanges {
  title: string;
  kind: 'questions' | 'repeating' | 'grid';
  expectedRows?: number | undefined;
  rowEntities?: GridRow[];
  gridLabelHeader?: string;
}

/**
 * What saving the section editor changes. A grid must have at least one row —
 * the printer refuses one without — so that is refused here, with the reason
 * to show, rather than silently saved as a question list.
 */
export function sectionEditUpdates(
  draft: SectionEditDraft,
  current: { title: string; rowEntities?: GridRow[] },
): { ok: true; updates: SectionEditChanges } | { ok: false; error: string } {
  const title = draft.title.trim() || current.title;
  if (draft.kind === 'grid') {
    const rowEntities = parseGridRowsText(draft.rowsText, current.rowEntities);
    if (rowEntities.length === 0) {
      return { ok: false, error: 'A grid needs at least one row. Enter one row per line.' };
    }
    return {
      ok: true,
      updates: {
        title, kind: 'grid', rowEntities,
        ...(draft.gridLabelHeader.trim() ? { gridLabelHeader: draft.gridLabelHeader.trim() } : {}),
      },
    };
  }
  if (draft.kind === 'repeating') {
    const n = parseInt(draft.expectedRows, 10);
    return { ok: true, updates: { title, kind: 'repeating', expectedRows: Number.isFinite(n) && n > 0 ? n : undefined } };
  }
  return { ok: true, updates: { title, kind: 'questions' } };
}
